import { Button } from '@suscri/ui';
import {
  Activity,
  ArrowRight,
  CalendarClock,
  CreditCard,
  Layers3,
  Rocket,
  PauseCircle,
  TrendingUp,
  Upload,
  WalletCards,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { EmptyState } from '../components/ui/EmptyState';
import { KpiCard } from '../components/ui/KpiCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { trpc } from '../lib/trpc';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enabled } = useAuthenticatedQuery();
  const { data: metrics, isLoading } = trpc.dashboard.metrics.useQuery(undefined, {
    enabled,
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader
          eyebrow={t('Panel de control')}
          title={t('Vista General')}
          description={t('Resumen de tus suscripciones y gastos')}
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="surface-card p-6">
              <div className="flex justify-between gap-4">
                <div className="space-y-4">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-9 w-32" />
                </div>
                <Skeleton className="h-11 w-11 rounded-xl" />
              </div>
              <Skeleton className="mt-7 h-4 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const totalSubscriptions =
    metrics.subscriptionCounts.active +
    metrics.subscriptionCounts.paused +
    metrics.subscriptionCounts.canceled;
  const activePercentage = totalSubscriptions
    ? Math.round((metrics.subscriptionCounts.active / totalSubscriptions) * 100)
    : 0;

  return (
    <div>
      <PageHeader
        eyebrow={t('Panel de control')}
        title={t('Vista General')}
        description={t('Resumen de tus suscripciones y gastos')}
        actions={
          <Link to="/subscriptions">
            <Button>
              <CreditCard className="h-4 w-4" />
              {t('Ver suscripciones')}
            </Button>
          </Link>
        }
      />

      {totalSubscriptions === 0 && (
        <EmptyState
          className="mb-6"
          icon={Rocket}
          title={t('¡Bienvenido a SUSCRI.net!')}
          description={t(
            'Empieza añadiendo tu primera suscripción, o importa varias de golpe desde un CSV/Excel si ya las tienes en una hoja de cálculo.'
          )}
          primaryAction={{
            label: t('Añadir mi primera suscripción'),
            onClick: () => navigate('/subscriptions'),
          }}
          secondaryAction={
            <Link to="/subscriptions">
              <Button variant="outline">
                <Upload className="h-4 w-4" />
                {t('Importar suscripciones')}
              </Button>
            </Link>
          }
        />
      )}

      <section aria-label={t('Métricas principales')} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={WalletCards}
          label={t('Gasto Recurrente')}
          value={`€${metrics.monthlyRecurring.toFixed(2)}`}
          helper={t('Equivalente mensual de tus suscripciones (anuales ÷ 12)')}
          badge="€/mes"
        />
        <KpiCard
          icon={Activity}
          accent="secondary"
          label={t('Gasto Mensual')}
          value={`€${metrics.monthlySpend.toFixed(2)}`}
          helper={t('Este mes')}
          badge="€/mes"
        />
        <KpiCard
          icon={TrendingUp}
          accent="warning"
          label={t('Gasto YTD')}
          value={`€${metrics.ytdSpend.toFixed(2)}`}
          helper={t('Año actual')}
          badge="€/año"
        />
        <KpiCard
          icon={CalendarClock}
          accent="info"
          label={t('Próximas Renovaciones')}
          value={metrics.upcomingRenewals.next7Days}
          helper={t('En los próximos 7 días')}
          badge={t('7 días')}
        />
      </section>

      <section className="mt-6 grid gap-6 xl:grid-cols-12">
        <article className="surface-card xl:col-span-5">
          <div className="flex items-center justify-between border-b border-border p-5 sm:p-6">
            <div>
              <p className="page-eyebrow">{t('Suscripciones')}</p>
              <h2 className="text-lg font-semibold text-foreground">{t('Estado de Suscripciones')}</h2>
            </div>
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Layers3 className="h-5 w-5" aria-hidden="true" />
            </span>
          </div>
          <div className="p-5 sm:p-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-3xl font-bold tracking-tight text-foreground">{totalSubscriptions}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t('Total suscripciones')}</p>
              </div>
              <span className="status-badge border-success/20 bg-success/10 text-success">
                {activePercentage}% {t('Activas').toLocaleLowerCase()}
              </span>
            </div>
            <div className="mt-5 h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
              <div className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${activePercentage}%` }} />
            </div>
            <dl className="mt-6 grid gap-3 sm:grid-cols-3">
              <StatusItem label={t('Activas')} value={metrics.subscriptionCounts.active} icon={Activity} tone="success" />
              <StatusItem label={t('Pausadas')} value={metrics.subscriptionCounts.paused} icon={PauseCircle} tone="warning" />
              <StatusItem label={t('Canceladas')} value={metrics.subscriptionCounts.canceled} icon={XCircle} tone="danger" />
            </dl>
          </div>
        </article>

        <article className="surface-card xl:col-span-4">
          <div className="border-b border-border p-5 sm:p-6">
            <p className="page-eyebrow">{t('Calendario')}</p>
            <h2 className="text-lg font-semibold text-foreground">{t('Próximas Renovaciones')}</h2>
          </div>
          <div className="space-y-3 p-5 sm:p-6">
            <RenewalRow label={t('Próximos 7 días')} value={metrics.upcomingRenewals.next7Days} tone="primary" />
            <RenewalRow label={t('Próximos 30 días')} value={metrics.upcomingRenewals.next30Days} tone="secondary" />
            <Link
              to="/calendar"
              className="mt-3 flex items-center justify-between rounded-xl border border-border px-4 py-3 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:bg-accent"
            >
              {t('Ver calendario')}
              <ArrowRight className="h-4 w-4 text-primary" aria-hidden="true" />
            </Link>
          </div>
        </article>

        <article className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/20 via-card to-secondary/10 p-5 shadow-card xl:col-span-3 sm:p-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
          <div className="relative">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-primary/20 bg-primary/15 text-primary">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="mt-8 text-sm font-medium text-muted-foreground">{t('Gasto promedio/mes')}</p>
            <p className="mt-2 text-4xl font-bold tracking-[-0.04em] text-foreground">€{metrics.monthlySpend.toFixed(2)}</p>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{t('Consulta los reportes para entender la evolución de tus gastos.')}</p>
            <Link to="/reports" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80">
              {t('Ver reportes')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </article>
      </section>
    </div>
  );
}

const statusTones = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  danger: 'bg-danger/10 text-danger',
};

function StatusItem({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: typeof Activity;
  tone: keyof typeof statusTones;
}) {
  return (
    <div className="rounded-xl border border-border bg-background/45 p-3">
      <dt className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${statusTones[tone]}`}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        {label}
      </dt>
      <dd className="mt-2 text-xl font-bold text-foreground">{value}</dd>
    </div>
  );
}

function RenewalRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'primary' | 'secondary';
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-background/45 p-4">
      <div className="flex items-center gap-3">
        <span className={`h-2.5 w-2.5 rounded-full ${tone === 'primary' ? 'bg-primary' : 'bg-secondary'}`} />
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      <span className="text-xl font-bold text-foreground">{value}</span>
    </div>
  );
}
